import { useEffect, useState } from "react";

/**
 * What's new, release by release (2026-10-11). Shown on the What's new page
 * and in the sidebar dot. Written for pilots, not developers: say what a pilot
 * sees or does differently, leave out how.
 *
 * How to keep it:
 *   - Add what you change to the top entry while it has `date: null`. An
 *     entry without a date is the next release and is not shown.
 *   - When releasing, give it the release date. release-desktop.sh refuses to
 *     publish a version that has no dated entry here, and changelog.test.js
 *     checks desktop/package.json's version is listed.
 *   - Changes to the web app reach pilots in a browser straight away but reach
 *     the desktop app with its next release; list them under that release.
 *
 * kind: "new" | "improved" | "fixed"
 */
export const RELEASES = [
  {
    version: "0.9.12",
    items: [
      ["fixed", "Dashboard: the Rank tile uses the same lettering and size as Total time, Hub and Assignments. It used to be smaller and in a different font."],
      ["fixed", "Flight Plan: the boxes use one text size, labels use one font everywhere (Runway, Elevation and Trans used to look like data), and all buttons match."],
    ],
  },
  {
    version: "0.9.11",
    date: "2026-10-15",
    items: [
      ["fixed", "Altitude now matches your altimeter. VACARS5 used to show the aircraft's true height above sea level, which can differ by several hundred feet at cruise (33,300 ft shown while the altimeter read 32,600 on standard)."],
      ["fixed", "The cruise announcement and cruise detection compare the altimeter with your planned flight level, so they no longer wait on a warm or cold day."],
      ["new", "Admins: open or close beta sign-up from the Beta applications page, with an optional message for visitors. Closing keeps testers and past applications."],
      ["fixed", "A step down for traffic in the middle of the flight stays cruise. It used to count as the descent for the rest of the flight and play the descent announcement early."],
    ],
  },
  {
    version: "0.9.10",
    date: "2026-10-11",
    items: [
      ["new", "Beta sign-up: pilots apply at acars.flyvac.org/apply, and the sign-in page links there when the closed beta turns someone away."],
      ["new", "Admins: a Beta applications page to approve or decline applications and manage the tester list."],
    ],
  },
  {
    version: "0.9.9",
    date: "2026-10-11",
    items: [
      ["new", "What's new: this page. A dot in the sidebar marks a release you haven't read about yet."],
      ["new", "Pilot guide: one click from the sidebar and the sign-in page opens the latest guide (PDF)."],
      ["improved", "Live Map shows each flight's actual phase (pushback, climb, cruise, descent, approach and so on) instead of only boarding, taxi or in flight, and you can filter by phase."],
      ["fixed", "The sign-in page shows the version you're running instead of \"FlyVAC 5.0\"."],
    ],
  },
  {
    version: "0.9.8",
    date: "2026-10-08",
    items: [
      ["improved", "VACARS5 now lives at acars.flyvac.org. Nothing to do: the update installs itself and signs you in as before."],
      ["improved", "The download page is simply myvac.flyvac.org/downloads/vacars5/, no version number needed."],
    ],
  },
  {
    version: "0.9.7",
    date: "2026-10-07",
    items: [
      ["fixed", "Landing rate is the rate at touchdown, not the steepest point of the approach. The overlay, Live Tracking and your PIREP all show the same number."],
      ["fixed", "The cruise announcement waits for your planned cruise level, not the first ATC level-off on the climb."],
      ["fixed", "The arrival announcement waits until you've slowed to taxi speed, not the landing roll."],
      ["fixed", "Telemetry keeps flowing while MSFS is still loading, and VACARS5 recovers from a SimConnect data error by itself instead of needing Reconnect."],
      ["fixed", "Takeoff is no longer logged as a go-around."],
      ["fixed", "Discord status reconnects by itself after Discord is restarted."],
      ["fixed", "Aircraft names from some add-ons (Fenix and others) show cleanly instead of as internal codes."],
    ],
  },
  {
    version: "0.9.6",
    date: "2026-10-06",
    items: [
      ["new", "The VAC 2026 look, matching the new myVAC and the dispatcher dashboard, in light and dark."],
      ["improved", "The Beta test page reminds testers to bid on the new myVAC at myvac.flyvac.org, not vacanada.org."],
    ],
  },
  {
    version: "0.9.5",
    date: "2026-10-05",
    items: [
      ["new", "Beta test page: a checklist to work through on a test flight, sent to the team with one click."],
    ],
  },
  {
    version: "0.9.4",
    date: "2026-10-05",
    items: [
      ["new", "Your flown route is saved with the flight, so the map on your myVAC flight record shows it, as with VACARS3."],
      ["fixed", "Live Tracking's event log and route stay put when you visit another page and come back."],
      ["fixed", "Pushback is no longer mistaken for a climb."],
    ],
  },
  {
    version: "0.9.3",
    date: "2026-10-04",
    items: [
      ["new", "Flight Plan shows your assigned passenger load beside SimBrief's."],
      ["improved", "Dashboard bids show departure and arrival times, marked as local time."],
      ["fixed", "Cabin audio keeps playing when you change page."],
      ["fixed", "The aircraft from your bid is filed with the flight, and Rouge bids find their aircraft on Init Flight."],
    ],
  },
  {
    version: "0.9.2",
    date: "2026-10-04",
    items: [
      ["fixed", "Mac copies can find updates again. On Windows, VACARS5 asks before restarting to install one."],
      ["fixed", "Levelling off during the descent no longer ends the descent phase."],
    ],
  },
  {
    version: "0.9.1",
    date: "2026-10-04",
    items: [
      ["improved", "IN time is when the parking brake is set and the engines are off, as in VACARS3."],
      ["improved", "Bids you place after opening VACARS5 show up without restarting it."],
      ["fixed", "Several fixes from the first full MSFS test flight."],
    ],
  },
  {
    version: "0.9.0",
    date: "2026-10-03",
    items: [
      ["new", "First beta release. Updates download from myvac.flyvac.org by themselves on Windows; on a Mac, VACARS5 tells you when there's a new version to download."],
      ["new", "The version you're running is shown at the bottom of the sidebar."],
    ],
  },
];

/** Released entries only (those with a date), newest first. */
export const PUBLISHED = RELEASES.filter((r) => r.date);

export const LATEST = PUBLISHED[0]?.version || null;

/**
 * "0.9.10" > "0.9.9". Plain string comparison gets that wrong, and the dot in
 * the sidebar would stop appearing at the tenth patch release.
 */
export function compareVersions(a, b) {
  const pa = String(a).split(".").map(Number);
  const pb = String(b).split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d) return d;
  }
  return 0;
}

const SEEN_KEY = "vacars5_whats_new_seen";
const SEEN_EVENT = "vacars5:whats-new-seen";

/** The newest version this browser/install has opened the What's new page for. */
export function seenVersion() {
  try { return localStorage.getItem(SEEN_KEY); } catch { return null; }
}

export function markSeen(version = LATEST) {
  try { if (version) localStorage.setItem(SEEN_KEY, version); } catch { /* storage off: dot stays */ }
  window.dispatchEvent(new Event(SEEN_EVENT));
}

/** Is there a release the pilot hasn't looked at? */
export function hasUnseen(seen = seenVersion()) {
  return !!LATEST && (!seen || compareVersions(LATEST, seen) > 0);
}

/** For the sidebar dot: true until the What's new page is opened. */
export function useUnseenRelease() {
  const [unseen, setUnseen] = useState(() => hasUnseen());
  useEffect(() => {
    const update = () => setUnseen(hasUnseen());
    window.addEventListener(SEEN_EVENT, update);
    // A child's effects run before its parent's, so landing straight on the
    // page marks it seen before this listener exists. Look again now.
    update();
    return () => window.removeEventListener(SEEN_EVENT, update);
  }, []);
  return unseen;
}
